import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from 'react-oidc-context';
import { authenticateWithContexture, clearAuthState } from '../../redux/slices/authSlice';
import { fetchProfilesMeta } from '../../redux/slices/profilesSlice';
import { fetchIntegrations } from '../../redux/slices/integrationsSlice';
import { ensureContextureMetadataForRepos } from '../../redux/slices/contextureMetadataSlice';
import styled from 'styled-components';
import { fetchCveFields } from '../../redux/slices/cveFieldsSlice';
import { useNormalizeHashUrl } from '../../hooks/useNormalizeHashUrl';
import {
  selectAuthIsFetching,
  selectAuthUser,
  selectAuthProfiles,
} from '../../redux/selectors/authSelectors';
import {
  selectApplicationProfile,
  selectUserProfile,
} from '../../redux/selectors/profileSelectors';
import { selectCveFieldsState } from '../../redux/selectors/cveFieldsSelectors';

const CenterLayout = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

const AuthWrapper = (props) => {
  const dispatch = useDispatch();
  const oidcAuth = useAuth();
  useNormalizeHashUrl(oidcAuth);
  const isFetching = useSelector(selectAuthIsFetching);
  const user = useSelector(selectAuthUser);
  const authProfiles = useSelector(selectAuthProfiles);
  const userProfile = useSelector(selectUserProfile);
  const applicationProfile = useSelector(selectApplicationProfile);
  const cveFields = useSelector(selectCveFieldsState);
  const signInInitiatedRef = useRef(false);
  useEffect(() => {
    if (!oidcAuth.isLoading && oidcAuth.isAuthenticated && !oidcAuth.error) {
      if (!user && !isFetching && oidcAuth.user) {
        dispatch(authenticateWithContexture(oidcAuth.user));
      }
    }
  }, [oidcAuth.isLoading, oidcAuth.isAuthenticated, oidcAuth.error, oidcAuth.user, user, isFetching, dispatch]);


  // Once auth slice has profiles and user, populate profiles & integrations slices metadata
  useEffect(() => {
    if (
      user &&
      authProfiles &&
      userProfile === null &&
      applicationProfile === null
    ) {
      dispatch(fetchProfilesMeta());
    }
  }, [user, authProfiles, userProfile, applicationProfile, dispatch]);

  useEffect(() => {
    if (
      user &&
      authProfiles &&
      !cveFields.isFetching &&
      cveFields.cveFields?.length === 0
    ) {
      dispatch(fetchCveFields());
    }
  }, [user, authProfiles, cveFields.isFetching, cveFields.cveFields, dispatch]);

  useEffect(() => {
    if (user && user?.profiles?.length) {
      dispatch(fetchIntegrations());
    }
  }, [user, user?.profiles?.length, dispatch]);

  // Centralized: Pre-load all contexture metadata for all user repos to prevent multiple API calls later
  useEffect(() => {
    if (user && user?.profiles?.length && authProfiles) {
      // Get all unique repo IDs from user profiles
      const repoIds = user.profiles
        .map(profile => profile.repositoryId)
        .filter(Boolean);
      
      if (repoIds.length > 0) {
        // Dispatch once to load all metadata, preventing individual components from making multiple calls
        dispatch(ensureContextureMetadataForRepos(repoIds));
      }
    }
  }, [user, user?.profiles?.length, authProfiles, dispatch]);

  // Kick off sign-in flow when not authenticated
  useEffect(() => {
    if (!oidcAuth.isLoading && !oidcAuth.isAuthenticated && !oidcAuth.error) {
      if (!signInInitiatedRef.current) {
        signInInitiatedRef.current = true;
        oidcAuth.signinRedirect();
      }
    }
    // On auth error, avoid forcing a reload loop; allow UI to render loader
  }, [oidcAuth.isLoading, oidcAuth.isAuthenticated, oidcAuth.error, oidcAuth]);

  // If we land on the app with an auth error (e.g. stale/missing state because
  // the flow started from a bookmarked Cognito URL), clean up the URL and
  // restart the sign-in so the library can generate fresh PKCE state.
  useEffect(() => {
    const hasAuthParams =
      window.location.search.includes('code=') &&
      window.location.search.includes('state=');

    if (!oidcAuth.error || oidcAuth.isLoading || oidcAuth.isAuthenticated || signInInitiatedRef.current || !hasAuthParams) return;

    if (!signInInitiatedRef.current) {
      signInInitiatedRef.current = true;
      oidcAuth.signinRedirect();
    }
  }, [oidcAuth.error, oidcAuth.isLoading, oidcAuth.isAuthenticated, oidcAuth]);

  // Listen for global auth-failure events and clear Redux + Contexture auth, then restart sign-in
  useEffect(() => {
    const handleAuthFailure = () => {
      dispatch(clearAuthState());
      oidcAuth.signinRedirect();
    };

    window.addEventListener('auth-failure', handleAuthFailure);

    return () => {
      window.removeEventListener('auth-failure', handleAuthFailure);
    };
  }, [dispatch, oidcAuth]);

  // When OIDC becomes unauthenticated (and not loading), clear Contexture/Redux auth state
  useEffect(() => {
    if (!oidcAuth.isAuthenticated && !oidcAuth.isLoading) {
      dispatch(clearAuthState());
    }
  }, [oidcAuth.isAuthenticated, oidcAuth.isLoading, dispatch]);

  if (oidcAuth.isLoading) {
    return (
      <CenterLayout>
        <div className='preloader'></div>
      </CenterLayout>
    );
  }

  return <> {props.children} </>;
};

export default AuthWrapper;
